import { useEffect, useRef } from 'react';
import { useAuthStore } from '../../store/authStore';
import { useTeamsStore } from '../../store/teamsStore';
import { useBoardsUiStore } from '../../extensions/bundled/task-manager/boardsUiStore';
import { roleForTeam } from '../../extensions/bundled/task-manager/boardRoles';
import { createdByFillFor } from '../../extensions/bundled/task-manager/boardCreatedBy';
import { loadTeamBoardOwners } from '../../extensions/bundled/task-manager/teamBoardOwners';

/**
 * Fill `createdBy` into an open board made before 1.0.7 (Boards Redesign
 * extras §2.8, owner-approved 12.10.2026): ONE normal board edit, so it syncs
 * to the user's other devices and to the team like any other change.
 *
 * - private board → the signed-in user;
 * - team board → whoever the server says created / shared it in
 *   (teamBoardOwners.js). A server without that field (before the backend
 *   deploy) knows nobody → nothing is written.
 *
 * Never for a viewer (the server rejects a viewer's push, and a rejected op
 * jams the scope's outbox), never while the team role is still unknown, never
 * twice for the same board in this tab, and without bumping `updatedAt` or
 * the undo history — it is not something the user did.
 */
export default function useBoardCreatedByFill({ board, readOnly, updateBoard }) {
  const myId = useAuthStore((s) => (s.user?.id != null ? String(s.user.id) : null));
  const teams = useTeamsStore((s) => s.teams);
  const teamId = board?.visibility === 'team' && board?.teamId != null ? String(board.teamId) : null;
  const teamOwners = useBoardsUiStore((s) => (teamId ? s.teamBoardOwners[teamId] : undefined));
  const filledRef = useRef(new Set());
  const boardId = board?.id ?? null;
  const missing = !!board && board.createdBy == null;

  useEffect(() => {
    if (missing && teamId && !teamOwners) loadTeamBoardOwners(teamId);
  }, [missing, teamId, teamOwners]);

  useEffect(() => {
    if (!missing || readOnly || !boardId || filledRef.current.has(boardId)) return;
    if (teamId) {
      const role = roleForTeam(teams, teamId);
      if (!role || role === 'viewer') return;
    }
    const fill = createdByFillFor(board, { myId, teamOwners });
    if (!fill) return;
    filledRef.current.add(boardId);
    updateBoard(
      (prev) => (prev?.id !== boardId || prev.createdBy != null ? prev : { ...prev, createdBy: fill }),
      { history: false, touch: false }
    );
  }, [missing, readOnly, boardId, teamId, teams, myId, teamOwners, board, updateBoard]);
}
